import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { listSessions, deleteSession, deletePhoto, getConfig, updateConfig, updateSession, buildGalleryUrl } from '../utils/api.js'
import { Download, Plus, ArrowLeft, Trash2, Image, Users, Camera, Pencil } from 'lucide-react'
import './Admin.css'

export default function Admin() {
  const navigate = useNavigate()
  const [sessions, setSessions] = useState([])
  const [config, setConfig] = useState(null)
  const [churchName, setChurchName] = useState('')
  const [brandingSaveStatus, setBrandingSaveStatus] = useState(null) // 'saved' | 'error' | null
  const brandingStatusTimer = useRef(null)
  const [loading, setLoading] = useState(true)

  // Per-session inline delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)

  // Per-session inline edit
  const [editingSessionId, setEditingSessionId] = useState(null)
  const [editFormData, setEditFormData] = useState({ family_name: '', email: '' })
  const [editSaveStatus, setEditSaveStatus] = useState(null) // 'saving' | 'error' | null

  useEffect(() => {
    loadData()
    return () => clearTimeout(brandingStatusTimer.current)
  }, [])

  async function loadData() {
    try {
      const [sessionsData, configData] = await Promise.all([
        listSessions(),
        getConfig()
      ])
      setSessions(sessionsData)
      setConfig(configData)
      setChurchName(configData?.church_name ?? '')
    } catch (err) {
      console.error('Failed to load admin data:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleChurchNameSubmit(event) {
    event.preventDefault()
    const trimmed = churchName.trim()
    if (!trimmed) return

    // Optimistic update: show success immediately, save in background
    const previousConfig = config
    const previousName = config?.church_name ?? ''
    setConfig(prev => ({ ...prev, church_name: trimmed }))
    setBrandingSaveStatus('saved')
    clearTimeout(brandingStatusTimer.current)
    brandingStatusTimer.current = setTimeout(() => setBrandingSaveStatus(null), 3000)

    try {
      const updatedConfig = await updateConfig({ church_name: trimmed })
      setConfig(updatedConfig)
      setChurchName(updatedConfig.church_name ?? '')
    } catch (err) {
      console.error('Failed to save church name:', err)
      // Revert optimistic update
      setConfig(previousConfig)
      setChurchName(previousName)
      clearTimeout(brandingStatusTimer.current)
      setBrandingSaveStatus('error')
    }
  }

  async function handleDeletePhoto(photoId, storagePath, sessionId) {
    if (!confirm('Delete this photo?')) return
    try {
      await deletePhoto(photoId, storagePath)
      setSessions(sessions.map(s => {
        if (s.id === sessionId) {
          return { ...s, photos: s.photos.filter(p => p.id !== photoId) }
        }
        return s
      }))
    } catch (err) {
      console.error('Failed to delete photo:', err)
    }
  }

  // --- Per-session delete ---
  async function confirmDeleteSession(sessionId) {
    const session = sessions.find(s => s.id === sessionId)
    try {
      await deleteSession(sessionId, session?.photos || [])
      setSessions(prev => prev.filter(s => s.id !== sessionId))
    } catch (err) {
      console.error('Failed to delete session:', err)
    } finally {
      setConfirmDeleteId(null)
    }
  }

  function handleEditStart(session) {
    setEditingSessionId(session.id)
    setEditFormData({ family_name: session.family_name, email: session.email ?? '' })
    setEditSaveStatus(null)
  }

  function handleEditCancel() {
    setEditingSessionId(null)
    setEditFormData({ family_name: '', email: '' })
    setEditSaveStatus(null)
  }

  async function handleEditSave(sessionId) {
    const trimmedName = editFormData.family_name.trim()
    if (!trimmedName) return
    setEditSaveStatus('saving')
    try {
      const updated = await updateSession(sessionId, {
        family_name: trimmedName,
        email: editFormData.email.trim() || null,
      })
      setSessions(prev => prev.map(s => s.id === sessionId
        ? { ...s, family_name: updated.family_name, email: updated.email }
        : s
      ))
      setEditingSessionId(null)
      setEditFormData({ family_name: '', email: '' })
      setEditSaveStatus(null)
    } catch (err) {
      console.error('Failed to update session:', err)
      setEditSaveStatus('error')
    }
  }

  function exportCSV() {
    const header = ['Session ID', 'Family Name', 'Email', 'Frame', 'Photos', 'Views', 'Created', 'Gallery Link']
    const rows = sessions.map(s => [
      s.id,
      s.family_name,
      s.email || '',
      s.frame_style,
      s.photos?.length || 0,
      s.view_count ?? 0,
      new Date(s.created_at).toLocaleString(),
      buildGalleryUrl(config?.gallery_base_url, s.id)
    ])
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    a.download = `easter-photobooth-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
  }

  function gotoNewSession() { navigate('/booth') }
  function gotoSetup() { navigate('/') }

  const totalPhotos = sessions.reduce((sum, s) => sum + (s.photos?.length || 0), 0)

  if (loading) {
    return (
      <div className="admin-screen">
        <div className="loading">Loading sessions...</div>
      </div>
    )
  }

  return (
    <div className="admin-screen">
      <div className="inner">
        <div className="admin-header">
          <div>
            <h1>All Sessions</h1>
            <p className="meta">
              <Users size={14} /> {sessions.length} session{sessions.length !== 1 ? 's' : ''}
              <span className="divider-dot" />
              <Image size={14} /> {totalPhotos} photos total
            </p>
          </div>
          <div className="header-actions">
            <button className="btn" onClick={exportCSV}>
              <Download size={16} /> Export CSV
            </button>
            <button className="btn btn-primary" onClick={gotoNewSession}>
              <Plus size={16} /> New Session
            </button>
          </div>
        </div>

        <div className="config-card">
          <div className="config-copy">
            <p className="config-kicker">Church branding</p>
            <h2>Update Church Name</h2>
            <p className="config-note">
              Change the church name used across the event experience. This now lives in Admin,
              not the landing page.
            </p>
          </div>

          <form className="config-form" onSubmit={handleChurchNameSubmit}>
            <div className="config-field">
              <label className="config-label" htmlFor="church-name-admin">
                Church name
              </label>
              <input
                id="church-name-admin"
                type="text"
                value={churchName}
                onChange={e => setChurchName(e.target.value)}
                placeholder="Enter church name"
                className="config-input"
              />
            </div>

            <button className="btn btn-primary" type="submit">
              Save Branding
            </button>
            {brandingSaveStatus === 'saved' && (
              <span className="branding-save-msg branding-save-msg--success">✓ Saved!</span>
            )}
            {brandingSaveStatus === 'error' && (
              <span className="branding-save-msg branding-save-msg--error">✗ Failed to save</span>
            )}
          </form>
        </div>

        <div className="sessions-list">
          {sessions.length === 0 ? (
            <div className="empty-card">
              <Camera size={48} className="empty-icon" />
              <p>No sessions yet. Start with a family!</p>
              <button className="btn btn-primary" onClick={gotoNewSession}>
                <Plus size={16} /> Start First Session
              </button>
            </div>
          ) : (
            sessions.map(session => {
              const galleryUrl = buildGalleryUrl(config?.gallery_base_url, session.id)
              const isConfirming = confirmDeleteId === session.id
              const isEditing = editingSessionId === session.id
              return (
                <div key={session.id} className="session-card">
                  <div className="session-header">
                    <div className="session-info">
                      <div className="family-name">{session.family_name}</div>
                      <div className="session-meta">
                        <span className="badge mono">{session.id}</span>
                        <span className="divider-dot" />
                        <span>{session.email || <em>No email</em>}</span>
                      </div>
                      <div className="session-stats">
                        {session.photos?.length || 0} photo{session.photos?.length !== 1 ? 's' : ''}
                        <span className="divider-dot" />
                        {session.view_count ?? 0} view{(session.view_count ?? 0) !== 1 ? 's' : ''}
                        <span className="divider-dot" />
                        {new Date(session.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        <span className="divider-dot" />
                        Frame: {session.frame_style}
                      </div>
                      <a
                        className="gallery-link"
                        href={galleryUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {galleryUrl}
                      </a>
                    </div>

                    <div className="session-card-actions">
                      {isConfirming ? (
                        <div className="inline-confirm">
                          <span className="confirm-text">Delete session?</span>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => confirmDeleteSession(session.id)}
                          >
                            Yes, delete
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => setConfirmDeleteId(null)}
                          >
                            No, keep
                          </button>
                        </div>
                      ) : (
                        <div className="session-action-btns">
                          <button
                            className="btn btn-edit-outline btn-sm"
                            title="Edit session"
                            onClick={() => handleEditStart(session)}
                          >
                            <Pencil size={14} /> Edit
                          </button>
                          <button
                            className="btn btn-danger-outline btn-sm session-delete-btn"
                            title="Delete session"
                            onClick={() => setConfirmDeleteId(session.id)}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="session-qr">
                      <QRCodeSVG value={galleryUrl} size={58} level="L" />
                    </div>
                  </div>

                  {isEditing && (
                    <div className="session-edit-form">
                      <div className="session-edit-fields">
                        <div className="session-edit-field">
                          <label className="config-label" htmlFor={`edit-name-${session.id}`}>
                            Family name
                          </label>
                          <input
                            id={`edit-name-${session.id}`}
                            type="text"
                            className="config-input session-edit-input"
                            value={editFormData.family_name}
                            onChange={e => setEditFormData(prev => ({ ...prev, family_name: e.target.value }))}
                            placeholder="Family name"
                          />
                        </div>
                        <div className="session-edit-field">
                          <label className="config-label" htmlFor={`edit-email-${session.id}`}>
                            Email
                          </label>
                          <input
                            id={`edit-email-${session.id}`}
                            type="email"
                            className="config-input session-edit-input"
                            value={editFormData.email}
                            onChange={e => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="Email (optional)"
                          />
                        </div>
                      </div>
                      <div className="session-edit-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEditSave(session.id)}
                          disabled={editSaveStatus === 'saving' || !editFormData.family_name.trim()}
                        >
                          {editSaveStatus === 'saving' ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={handleEditCancel}
                          disabled={editSaveStatus === 'saving'}
                        >
                          Cancel
                        </button>
                        {editSaveStatus === 'error' && (
                          <span className="branding-save-msg branding-save-msg--error">✗ Failed to save</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="photos-strip">
                    {session.photos?.map(photo => (
                      <div key={photo.id} className="photo-thumb">
                        <img
                          src={photo.public_url}
                          alt="Session photo"
                          onClick={() => window.open(photo.public_url, '_blank')}
                        />
                        <button
                          className="delete-btn"
                          onClick={() => handleDeletePhoto(photo.id, photo.storage_path, session.id)}
                          title="Delete photo"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    {session.photos?.length === 0 && (
                      <span className="no-photos">No photos yet</span>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <button className="btn btn-back" onClick={gotoSetup}>
          <ArrowLeft size={16} /> Setup
        </button>

        <div className="info-box">
          <p className="info-title">ℹ Storage Info</p>
          <p className="info-text">
            Photos are stored in Supabase Storage. Each session gets a unique 6-character code.
            Families can access their gallery via the shareable link or QR code.
          </p>
        </div>
      </div>
    </div>
  )
}
